import { Fragment, useState } from "react";
import styled from "styled-components";
import { CalendarList } from "@lifeat/shared/calendar/types/CalendarList";
import { CalendarDetails } from "@lifeat/shared/data-models/calendar/CalendarDetails";
import { CalendarProvider } from "@lifeat/shared/data-models/user/integrations/IntegrationsDataEntity";
import CaretIconToggle from "@common/Icons/CaretIconToggle";
import { DropdownMenuComponents } from "@common/LifeAtDropdownMenuV2/DropdownMenuComponents";
import { CallToActionContextType, useCallToActionContext } from "@contexts/CallToActionContext";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import CalendarWidgetToolbarIcon from "@icons/WidgetToolbar/Calendar-ToolbarWidget-Icon.svg";
import GoogleLogo from "@resources/logos/integrations/Google/google_logo.png";
import OutlookLogo from "@resources/logos/integrations/Outlook/outlook_logo.png";
import { UpgradeCTASource } from "@user/AmplitudeProSubsEvents";
import CalendarSelectorElement from "./CalendarSelectorElement";
import ChevronRightIcon from "/resources/icons/ChevronRightArrow.svg";

const ADD_CALENDAR_LABEL = "Edit calendars";

const StyledCalendarIcon = styled(CalendarWidgetToolbarIcon)`
    fill: ${(props) => props.theme.iconColor};
`;

const AddNewCalendarFooter = styled.div<{ isDisabled: boolean }>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 100%;
    width: 100%;
    color: ${(props) => props.theme.textColorPrimary};
    cursor: pointer;

    opacity: ${(props) => (props.isDisabled ? 0.5 : 1.0)};
`;

const StyledChevronRightIcon = styled(ChevronRightIcon)`
    cursor: pointer;
    fill: ${(props) => props.theme.iconColor};
`;

const CalendarSelectorContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 2px;
`;

const ConnectionLabel = styled(DropdownMenuComponents.Label)`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    font-weight: bold;
`;

const IconImg = styled.img`
    width: 16px;
    height: auto;
`;

interface CalendarSelectorProps {
    selectedCalendarList: CalendarDetails[];
    calendars: CalendarList;
    onChange: (calendar: CalendarDetails, selected: boolean) => void;
    openSettings: () => void;
    className?: string;
}
function CalendarSelector(props: CalendarSelectorProps) {
    const { userData }: UserContextType = useUserContext();
    const { setUpgradeCtaContext }: CallToActionContextType = useCallToActionContext();

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const handleSelectCalendarClick = (calendar: CalendarDetails) => {
        // TODO: only update this state until the dropdown is closed
        props.onChange(calendar, !isActive(calendar));
    };

    const isActive = (calendar: CalendarDetails) => {
        for (const selected of props.selectedCalendarList) {
            if (
                selected.provider === calendar.provider &&
                selected.email === calendar.email &&
                selected.calendarId === calendar.calendarId
            ) {
                return true;
            }
        }
        return false;
    };

    const handleClickAddCalendarButton = () => {
        if (userData?.proUserInfo?.isProUser) {
            props.openSettings();
        } else {
            setUpgradeCtaContext("multipleCalendars", UpgradeCTASource.CALENDAR_SELECTOR);
        }
    };

    return (
        <DropdownMenuComponents.Root
            open={isDropdownOpen}
            onOpenChange={(isOpen: boolean) => setIsDropdownOpen(isOpen)}
        >
            <DropdownMenuComponents.Trigger>
                <CalendarSelectorContainer>
                    <StyledCalendarIcon className={`calendar-selector-icon`} />
                    <CaretIconToggle showOpen={isDropdownOpen} />
                </CalendarSelectorContainer>
            </DropdownMenuComponents.Trigger>
            <DropdownMenuComponents.Content>
                {Object.entries(props.calendars).map(([provider, byEmail]) =>
                    Object.entries(byEmail).map(([email, byId]) => (
                        <Fragment key={`${provider}-${email}`}>
                            <ConnectionLabel>
                                <IconImg
                                    src={(() => {
                                        switch (provider) {
                                            case CalendarProvider.GOOGLE:
                                                return GoogleLogo;
                                            case CalendarProvider.OUTLOOK:
                                                return OutlookLogo;
                                        }
                                    })()}
                                />
                                {email}
                            </ConnectionLabel>
                            {Object.entries(byId).map(([calendarId, calendar]) => (
                                <DropdownMenuComponents.Item
                                    key={calendarId}
                                    onClick={() => handleSelectCalendarClick(calendar)}
                                >
                                    <CalendarSelectorElement
                                        calendar={calendar}
                                        isActive={isActive(calendar)}
                                        blockClick={props.selectedCalendarList?.length === 1 && isActive(calendar)}
                                        onChange={(selected) => props.onChange(calendar, selected)}
                                    />
                                </DropdownMenuComponents.Item>
                            ))}
                        </Fragment>
                    )),
                )}

                <DropdownMenuComponents.Separator />
                <DropdownMenuComponents.Item key={`add-calendar-footer`} onClick={handleClickAddCalendarButton}>
                    <AddNewCalendarFooter isDisabled={!userData?.proUserInfo?.isProUser}>
                        {ADD_CALENDAR_LABEL}
                        <StyledChevronRightIcon />
                    </AddNewCalendarFooter>
                </DropdownMenuComponents.Item>
            </DropdownMenuComponents.Content>
        </DropdownMenuComponents.Root>
    );
}
export default CalendarSelector;
